import dayjs from "dayjs";
import { Track } from "livekit-client";
import { NAMED_TIMEZONES } from "~/constants/named-timezones";
import type { useMiddleware } from "~/store/middleware";
import { ItemKeysValues, rowStatuses } from "~/types/app";
import { editableStatuses, pdfEditionStatuses } from "~/types/global";
import { SessionKeyStatus } from "~/types/session";

// Array of special subdomains that should not be treated as user accounts
export const SPECIAL_SUBDOMAINS = ["app", "uat"];

// Function to check if a subdomain is a special subdomain
export function isSpecialSubdomain(subdomain: string): boolean {
  return SPECIAL_SUBDOMAINS.includes(subdomain);
}

export function getSourceIcon(source: Track.Source, enabled: boolean) {
  switch (source) {
    case Track.Source.Microphone:
      return enabled
        ? "system-uicons:microphone"
        : "system-uicons:microphone-disabled";
    case Track.Source.Camera:
      return enabled
        ? "system-uicons:camera-alt"
        : "system-uicons:camera-noflash-alt";
    case Track.Source.ScreenShare:
      return enabled ? "tabler:screen-share" : "tabler:screen-share-off";
  }
}

export function formatPhone(phone: string) {
  let cleaned = ("" + phone).replace(/\D/g, "");

  let match = cleaned.match(/^(1|)?(\d{3})(\d{3})(\d{4})$/);

  if (match) {
    let intlCode = match[1] ? "+1 " : "";
    return [intlCode, "(", match[2], ") ", match[3], "-", match[4]].join("");
  }
  return null;
}

interface Timer {
  string: string;
  minutes: number;
  seconds: number;
  totalSeconds: number;
}

export function useCountDown(initialMinutes: number, onComplete?: () => void) {
  const min = ref(initialMinutes);
  const isRunning = ref(false);
  let intervalId: NodeJS.Timeout | null = null;

  const formatTimer = (value: number): Timer => {
    const minutes = Math.floor(value / 60);
    const seconds = value - minutes * 60;
    const totalSeconds = value;
    const string = `${minutes}:${seconds < 10 ? "0" : ""}${seconds}`;
    return { string, minutes, seconds, totalSeconds };
  };

  const timer = ref(formatTimer(min.value));

  const start = () => {
    if (!isRunning.value) {
      isRunning.value = true;
      intervalId = setInterval(() => {
        min.value--;
        timer.value = formatTimer(min.value);
        if (min.value === 0) {
          stop();
          if (onComplete) {
            onComplete();
          }
        }
      }, 1000);
    }
  };

  const pause = () => {
    if (isRunning.value) {
      isRunning.value = false;
      clearInterval(intervalId!);
      intervalId = null;
    }
  };

  const stop = () => {
    if (isRunning.value) {
      pause();
    }
    min.value = initialMinutes;
    timer.value = formatTimer(min.value);
  };

  const restart = () => {
    stop();
    start();
  };

  watch(
    () => min.value,
    (value) => {
      if (value === 0) {
        stop();
        if (onComplete) {
          onComplete();
        }
      }
    },
  );

  return {
    timer,
    isRunning,
    start,
    pause,
    stop,
    restart,
  };
}

export async function checkSubdomainExist(
  middleware: ReturnType<typeof useMiddleware>,
  subdomain: string,
) {
  middleware.subdomainData = subdomain;
  if (!middleware.subdomain || middleware.subdomainIsStale) {
    await middleware.subdomainRefetch();
  }
  if (!middleware.subdomain) {
    throw new Error("Subdomain does not exist in this tenant");
  }
}

function getFilenameFromUrl(url: string) {
  const urlObj = new URL(url);
  return urlObj.pathname.split("/").pop() ?? "";
}

export async function downloadFile(
  state: string,
  recordId: number,
  id: number,
) {
  const { $axios } = useNuxtApp();
  const responses = await $axios.get(
    `/v1/${state}_records/${recordId}/recordings/${id}/download`,
  );
  const presignedUrl = responses.data.presigned_url;

  const filename = getFilenameFromUrl(presignedUrl);
  const link = document.createElement("a");
  link.href = presignedUrl;
  link.setAttribute("download", filename);
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
}

/**
 * This functions validates if should enable the current menu option based on:
 *  - if the current option is Lock Document and the session is on status scheduled then must not enabled
 *  - if the current option is lock, edit, delete and the session status is not completed then enable it
 *  - if the current option is download, then the session status must be "complete" to enable it
 *  - others option will directly be enabled
 *
 * @param item Actual item to check
 * @param row The actual document to check
 * @param session The current session data
 */
export function shouldEnableMenuOption(item: any, row: any, session: any) {
  if (
    item.key === ItemKeysValues.LOCK &&
    (session?.session_status === SessionKeyStatus.SCHEDULED ||
      row.custom_properties.isLocking)
  ) {
    return false;
  }
  if (pdfEditionStatuses.includes(item.key)) {
    return editableStatuses.includes(row.custom_properties.status);
  }
  if (item.key === ItemKeysValues.DOWNLOAD) {
    return row.custom_properties.status === rowStatuses.COMPLETE;
  }
  return true;
}

export function notifyLockingDoc(docs, data: string) {
  const document = toRaw(docs.value).data.find(
    (document) => document.id === data,
  );
  if (document) {
    document.custom_properties.isLocking = true;
    triggerRef(docs);
  }
}

/**
 * Processes the download response and initiates the file download
 *
 * @param {Object} res - The axios response object containing the file data
 * @param {Object} data - The document data object
 * @param {Object} data.custom_properties - Custom properties of the document
 * @param {string} data.custom_properties.title - The title of the document to use for the filename
 * @returns {Promise<void>} A promise that resolves when the download is processed
 * @description
 * This function checks if the response is a valid file or an error message in JSON format.
 * If it's a valid file, it creates a download link and triggers the download.
 * If it's an error message, it rejects the promise with the error message.
 */
async function checkDownloadData(res, data) {
  if (res.data instanceof Blob && res.data.type === "application/json") {
    const checkIsBlobPromise = new Promise<typeof res>((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => {
        try {
          const jsonResponse = JSON.parse(reader.result as string);
          if (jsonResponse.success === false) {
            reject(new Error(jsonResponse.message || "Download failed"));
          } else {
            resolve(res);
          }
        } catch (e) {
          resolve(res); // Not a valid JSON, proceed as normal
        }
      };
      reader.onerror = () => resolve(res); // Error reading blob, proceed as normal
      reader.readAsText(res.data);
    });

    await checkIsBlobPromise;
  }

  const url = window.URL.createObjectURL(res.data);
  const link = document.createElement("a");
  link.href = url;
  link.setAttribute("download", data.custom_properties.title + ".pdf");
  document.body.appendChild(link);
  link.click();
}

/**
 * Handles the PDF download process for a document
 *
 * @param {Object} $axios - The axios instance for making HTTP requests
 * @param {Function} $reportError - Function to report errors
 * @param {Object} toast - Toast notification service
 * @param {string} session - The session ID
 * @param {Object} data - The document data object
 * @param {string} data.id - The ID of the document to download
 * @param {Object} data.custom_properties - Custom properties of the document
 * @returns {Promise<void>} A promise that resolves when the download is complete
 * @description
 * This function initiates a download request for a PDF document from the server.
 * It handles the download process, error reporting, and user notifications.
 * On success, it triggers the file download.
 * On failure, it displays an error message to the user.
 */
export async function handleDownloadPDF(
  $axios,
  $reportError,
  toast,
  session: string,
  data,
) {
  $axios
    .post(
      `/v1/sessions/${session}/files/${data.id}/download`,
      {},
      {
        responseType: "blob",
      },
    )
    .then((res) => checkDownloadData(res, data))
    .catch((error) => {
      $reportError("Error downloading document", error);
      toast.message({
        title: "Error",
        message:
          error.message ??
          "Something went wrong while downloading the document",
      });
    });
}

/**
 * This function returns the list of timezones with the offset. It will also add standard names to some of the timezones, such as "Central Time" for "America/Chicago".
 * @returns {Array} List of timezones with the offset
 */
export const getTimezones = () => {
  return Intl.supportedValuesOf("timeZone").filter((tz) => tz.startsWith("America/")).map((tz) => {
    const offset = dayjs().tz(tz).format("Z");
    const namedTimezone = NAMED_TIMEZONES[tz];
    const shownTz = tz.replaceAll("_", " ").replaceAll('/', ' - ');
    const label = `(UTC${offset}) ${shownTz}${namedTimezone ? ` (${namedTimezone})` : ""}`;
    return {
      label: label,
      value: tz,
    };
  });
};
